import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideHeader,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface HomePageStyles {
    homePageContainer?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    page?: MobiusPageProps;
}

const mapStateToProps = (state: ApplicationState) => ({
    addingProductToCart: state.context.addingProductToCart,
});

type Props = ReturnType<typeof mapStateToProps> & PageProps;

const styles: HomePageStyles = {
    homePageContainer: {
        css: css`
            &.homePageContainer {
                padding: 0;
                max-width: 100%;
                .container + div[class^="GridWrapper"] {
                    div[class^="sc"] {
                        -webkit-box-pack: center;
                        justify-content: center;
                    }
                }
                > div > div[class^="GridWrapper"] {
                    max-width: 100%;
                    > div[class^="GridOffset"] {
                        width: 100%;
                        margin-left: auto;
                        margin-right: auto;
                        > div[class^="GridItemStyle"] {
                            max-width: 100%;
                            margin: 0 auto;
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        null,
                                        css`
                                            max-width: 100%;
                                        `,
                                    ],
                                    "max",
                                )}
                        }
                        > div[class^="GridItemStyle"] {
                            &:first-child {
                                padding: 0;
                                max-width: 100%;
                                background: #f7f7f7;
                                padding-bottom: 40px;
                            }
                        }
                    }
                }
                div[data-test-selector="brandGallery"] + div[class^="sc"] {
                    -webkit-box-pack: center;
                    justify-content: center;
                }
                & div {
                    &.categoryTitle {
                        padding: 20px 0px;
                        max-width: 1440px;
                        margin: 0 auto;
                        & h3 {
                            font-size: 28px;
                            font-weight: 500;
                            color: ${getColor("secondary.main")};
                            width: 100%;
                            text-align: left;
                            border-bottom: 2px solid ${getColor("primary")};
                            line-height: 0.1em;
                            margin: 10px 0 20px;
                            & span {
                                background: #ffffff;
                                padding-right: 10px;
                            }
                        }
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        & h3 {
                                            font-size: 20px;
                                            &.blueMark {
                                                border-bottom: none;
                                                line-height: 1.5;
                                            }
                                        }
                                    `,
                                    css`
                                        & h3 {
                                            font-size: 20px;
                                        }
                                    `,
                                    null,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                    }
                }
            }
        `,
    },
    loadingOverlay: {
        css: css`
            & div[class^="SpinnerWrapper"] {
                position: fixed;
            }
        `,
    },
};

const HomePage: React.FunctionComponent<Props> = ({ id, addingProductToCart }: Props) => (
    <>
        <LoadingOverlay
            {...styles.loadingOverlay}
            loading={addingProductToCart !== undefined ? addingProductToCart : false}
        >
            <Page data-test-selector="homePage" {...styles.homePageContainer} className="homePageContainer">
                <Zone contentId={id} zoneName="Content" requireRows />
                <Modals />
            </Page>
        </LoadingOverlay>
    </>
);

const pageModule: PageModule = {
    component: connect(mapStateToProps, null)(HomePage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            HideHeader,
            HideFooter,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "HomePage" instead of this constant.
 */
export const HomePageContext = "HomePage";
